import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import {
  GetMeetingDocumentsV1MeetingsMeetingIdDocumentsGetParams,
  GetScheduledMeetingV1MeetingsMeetingIdGetParams,
  SendMeetingReminderV1MeetingsMeetingIdSendReminderPostParams,
} from 'compliance-api/generated.ts';
import { FormikErrors, FormikValues } from 'formik';
import { api } from '@/modules/common/api/index.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import {
  GetMeetingSummaryTemplatesResponse,
  GetScheduledMeetingResponse,
  ListScheduledMeetingsV1MeetingsGetParams,
  ScheduleMeetingRequest,
  ScheduleMeetingResponse,
  SendMeetingReminderResponse,
  UpdateScheduledMeetingResponse,
} from 'compliance-api';

export const MEETINGS_QUERY_KEY = 'vestra-compliance/get-meetings';
export const MEETING_BY_ID_QUERY_KEY = 'vestra-compliance/get-meeting-by-id';
export const MEETING_SUMMARY_TEMPLATES_QUERY_KEY =
  'vestra-compliance/get-meeting-summary-templates';
export const MEETING_RECALL_DOCUMENTS_QUERY_KEY = 'vestra-compliance/get-meeting-recall-documents';

export const useMeetingSummaryTemplatesQuery = (
  options: {
    onSuccess?: (templates: GetMeetingSummaryTemplatesResponse) => void;
    disabled?: boolean;
  } = {},
) => {
  const { onSuccess, disabled } = options;

  return useQuery({
    queryKey: [MEETING_SUMMARY_TEMPLATES_QUERY_KEY, useGetCurrentBusinessId()],
    staleTime: 1000 * 60 * 60 * 3,
    queryFn: async () => {
      const response = await api.v1.listMeetingSummaryTemplatesV1MeetingsTemplatesGet();
      return response.data;
    },
    onSuccess,
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useMeetingRecallDocumentsInfiniteQuery = (
  payload: GetMeetingDocumentsV1MeetingsMeetingIdDocumentsGetParams,
  options: {
    disabled?: boolean;
  } = {},
) => {
  const { disabled } = options;
  const { page, ...data } = payload;

  return useInfiniteQuery({
    queryKey: [MEETING_RECALL_DOCUMENTS_QUERY_KEY, JSON.stringify(data), useGetCurrentBusinessId()],
    staleTime: 1000 * 60 * 60 * 3,
    queryFn: async ({ pageParam }) => {
      const pageParamObject: Pick<
        GetMeetingDocumentsV1MeetingsMeetingIdDocumentsGetParams,
        'page' | 'page_size'
      > =
        typeof pageParam === 'number'
          ? { page: pageParam }
          : {
              page: pageParam?.page,
              page_size: pageParam?.page_size,
            };

      const response = await api.v1.getMeetingDocumentsV1MeetingsMeetingIdDocumentsGet({
        ...data,
        page: pageParamObject.page ?? page,
        page_size: pageParamObject.page_size ?? data.page_size,
      });
      return response.data;
    },
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled && !!payload.meetingId,
  });
};

export const useGetMeetingQuery = (
  payload: GetScheduledMeetingV1MeetingsMeetingIdGetParams,
  options: {
    onSuccess?: (meeting: GetScheduledMeetingResponse) => void;
    disabled?: boolean;
  } = {},
) => {
  const { onSuccess, disabled } = options;

  return useQuery({
    queryKey: [MEETING_BY_ID_QUERY_KEY, payload.meetingId, useGetCurrentBusinessId()],
    staleTime: 999 * 60 * 60,
    refetchInterval: 1000 * 60 * 5, //5min
    queryFn: async () => {
      const response = await api.v1.getScheduledMeetingV1MeetingsMeetingIdGet(payload);
      return response.data;
    },
    onSuccess,
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled && !!payload.meetingId,
  });
};

export const useGetMeetingsInfiniteQuery = (
  payload: ListScheduledMeetingsV1MeetingsGetParams = {},
  disabled = false,
) => {
  const { page, ...data } = payload;

  return useInfiniteQuery({
    queryKey: [MEETINGS_QUERY_KEY, JSON.stringify(data), useGetCurrentBusinessId()],
    staleTime: 999 * 60 * 60, // 1 hour
    refetchInterval: 1000 * 60 * 5, //5min
    queryFn: async ({ pageParam }) => {
      const pageParamObject: Pick<
        ListScheduledMeetingsV1MeetingsGetParams,
        'page' | 'page_size' | 'search'
      > =
        typeof pageParam === 'number'
          ? { page: pageParam }
          : {
              page: pageParam?.page,
              page_size: pageParam?.page_size,
              search: pageParam?.search,
            };

      const response = await api.v1.listScheduledMeetingsV1MeetingsGet({
        ...data,
        page: pageParamObject.page ?? page,
        page_size: pageParamObject.page_size ?? data.page_size,
        search: pageParamObject.search ?? data.search,
      });
      return response.data;
    },
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useCreateMeetingMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: (data: ScheduleMeetingResponse) => Promise<void>;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: ScheduleMeetingRequest) => {
      const response = await withErrorHandling(api.v1.scheduleMeetingV1MeetingsPost(payload), {
        loading: 'Creating meeting...',
        success: 'Meeting created',
        setErrors,
      });
      return response.data;
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({ queryKey: [MEETINGS_QUERY_KEY] });
      onSuccess && (await onSuccess(data));
    },
  });
};

export const useUpdateMeetingMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: (data: UpdateScheduledMeetingResponse) => Promise<void>;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: ScheduleMeetingRequest & { meetingId: number }) => {
      const response = await withErrorHandling(
        api.v1.updateMeetingV1MeetingsMeetingIdPut({ meetingId: payload.meetingId }, payload),
        {
          loading: 'Updating meeting...',
          success: 'Meeting updated',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({ queryKey: [MEETINGS_QUERY_KEY] });
      await queryClient.invalidateQueries({ queryKey: [MEETING_BY_ID_QUERY_KEY] });
      onSuccess && (await onSuccess(data));
    },
  });
};

export const useDeleteMeetingMutation = ({
  onSuccess,
}: {
  onSuccess?: () => void;
} = {}) => {
  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const response = await withErrorHandling(
        api.v1.deleteScheduledMeetingV1MeetingsMeetingIdDelete({ meetingId: id }),
        {
          loading: 'Deleting meeting...',
          success: 'Meeting deleted',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MEETINGS_QUERY_KEY] });
      onSuccess && onSuccess();
    },
  });
};

export const useSendMeetingReminderMutation = ({
  onSuccess,
}: {
  onSuccess?: (data: SendMeetingReminderResponse) => Promise<void> | void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: SendMeetingReminderV1MeetingsMeetingIdSendReminderPostParams) => {
      const response = await withErrorHandling(
        api.v1.sendMeetingReminderV1MeetingsMeetingIdSendReminderPost(payload),
        {
          loading: 'Sending reminder...',
          success: 'Reminder sent',
        },
      );
      return response.data;
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({ queryKey: [MEETINGS_QUERY_KEY] });
      await queryClient.invalidateQueries({ queryKey: [MEETING_BY_ID_QUERY_KEY] });
      onSuccess && (await onSuccess(data));
    },
  });
};
